<script setup lang="ts">
import { toast } from '@/composables/baseToast'
import { supabase } from '@/composables/supabaseClient'
import toastThrowError from '@/helpers/toastThrowError'
import uuidv7 from '@/helpers/uuidv7'
import { useDatabaseStore } from '@/stores/databaseStore'
import { computed, ref } from 'vue'
import BaseButton from './BaseButton.vue'
import type { DataTableColumn } from './BaseDataTable.vue'
import BaseDataTable from './BaseDataTable.vue'

const databaseStore = useDatabaseStore()
const selectedCostSource = ref(databaseStore.costSources?.find((c) => c))
const showNewInput = ref(false)
const newEquipmentTypeName = ref<string>()

const rows = computed(() => {
  return (
    databaseStore.equipmentTypes?.map((equipmentType) => {
      const equipmentRate = databaseStore.equipmentRates?.find(
        (equipmentRate) =>
          equipmentRate.equipment_type_id == equipmentType.id &&
          equipmentRate.cost_source_id == selectedCostSource.value?.id,
      )
      return {
        equipmentTypeId: equipmentType.id,
        equipmentTypeName: equipmentType.name,
        equipmentRateRate: equipmentRate?.rate,
        equipmentRateId: equipmentRate?.id,
      }
    }) ?? []
  )
})
type RowType = (typeof rows.value)[number]
const columns: DataTableColumn<RowType>[] = [
  {
    columnDataKey: 'equipmentTypeName',
    name: 'Name',
  },
  {
    columnDataKey: 'equipmentRateRate',
    name: 'Rate',
    columnType: 'number',
  },
]

async function addCostSource() {
  if (!newEquipmentTypeName.value) throw toastThrowError('New equipment type must have a name')
  const { data, error } = await supabase
    .from('equipment_type')
    .insert({ id: uuidv7(), name: newEquipmentTypeName.value })
    .select()
    .single()
  if (error) throw toastThrowError('Error saving new equipment type', error)
  databaseStore.equipmentTypes?.push(data)
  newEquipmentTypeName.value = undefined
}

async function updateEquipmentType(row: RowType) {
  const { data, error } = await supabase
    .from('equipment_type')
    .update({ name: row.equipmentTypeName })
    .eq('id', row.equipmentTypeId)
    .select()
    .single()
  if (error) throw toastThrowError('Error updating equipment type', error)
  const index = databaseStore.equipmentTypes?.findIndex((t) => t.id == row.equipmentTypeId)
  if (index == undefined) throw toastThrowError('Error updating equipment type. Index not found')
  if (!selectedCostSource.value) throw toastThrowError('Cost source must be selected')
  databaseStore.equipmentTypes?.splice(index, 1, data)
  if (row.equipmentRateId) {
    // Update
    if (!row.equipmentRateRate) {
      // Deleted equipment rate
      const rateResult = await supabase
        .from('equipment_rate')
        .delete()
        .eq('id', row.equipmentRateId)
      if (rateResult.error) throw toastThrowError('Error removing equipment rate', rateResult.error)
      const equipmentRateIndex = databaseStore.equipmentRates?.findIndex(
        (er) => er.id == row.equipmentRateId,
      )
      if (equipmentRateIndex == undefined)
        throw toastThrowError('Error: Unable to get equipment rate index')
      databaseStore.equipmentRates?.splice(equipmentRateIndex, 1)
      toast('Removed equipment type')
    } else {
      // Regular Update
      const { data: rateData, error: rateError } = await supabase
        .from('equipment_rate')
        .update({
          rate: row.equipmentRateRate,
        })
        .eq('id', row.equipmentRateId)
        .select()
        .single()
      if (rateError) throw toastThrowError('Error updating equipment rate', rateError)
      const equipmentRateIndex = databaseStore.equipmentRates?.findIndex(
        (er) => er.id == row.equipmentRateId,
      )
      if (equipmentRateIndex == undefined)
        throw toastThrowError('Error: Unable to get equipment rate index')
      databaseStore.equipmentRates?.splice(equipmentRateIndex, 1, rateData)
      toast('Updated equipment type')
    }
  } else {
    // Add new
    const { data: rateData, error: rateError } = await supabase
      .from('equipment_rate')
      .insert({
        id: uuidv7(),
        cost_source_id: selectedCostSource.value.id,
        equipment_type_id: row.equipmentTypeId,
        rate: row.equipmentRateRate,
      })
      .select()
      .single()
    if (rateError) throw toastThrowError('Error adding equipment rate', rateError)
    databaseStore.equipmentRates?.push(rateData)
    toast('Added equipment rate')
  }
}
</script>

<template>
  <div class="flex h-full w-full flex-col gap-3 p-3">
    <div class="flex">
      <label for="costSource">Cost Source: </label>
      <select v-model="selectedCostSource" class="bg-white focus:border">
        <option
          v-for="costSource in databaseStore.costSources"
          :key="costSource.id"
          :value="costSource"
        >
          {{ costSource.name }}
        </option>
      </select>
    </div>
    <div>
      <BaseDataTable
        table-name="DBEquipment"
        :columns="columns"
        :rows="rows"
        :row-key="'equipmentTypeId'"
        @row-update="updateEquipmentType"
      />
    </div>
    <div v-if="showNewInput" class="flex w-full flex-nowrap">
      <!-- New source input -->
      <input
        id="name"
        v-model="newEquipmentTypeName"
        class="w-full border p-1"
        placeholder="Cost Source Name"
      />
      <button @click="addCostSource()">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          class="size-6"
        >
          <path
            fill-rule="evenodd"
            d="M12 3.75a.75.75 0 0 1 .75.75v6.75h6.75a.75.75 0 0 1 0 1.5h-6.75v6.75a.75.75 0 0 1-1.5 0v-6.75H4.5a.75.75 0 0 1 0-1.5h6.75V4.5a.75.75 0 0 1 .75-.75Z"
            clip-rule="evenodd"
          />
        </svg>
      </button>
    </div>
    <BaseButton @click="showNewInput = true"> New Equipment Type </BaseButton>
  </div>
</template>
